import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../../../../components'
import { fetchShippingCoveragesList, bulkUpdateShippingCoveragesByIds, updateShippingCoverageByRange } from '../../../../services/shippingMethodsServices'
import { FieldLabel } from './formPrimitives'
import { CoverageJobProgressPanel } from '../CoverageJobProgressPanel'
import { MAX_RANGE, computeRange } from './zipRange'
import { UpdateCoverageForm } from './UpdateCoverageForm'
import {
  useMethodZones,
  ZoneSelect,
  ExtendedZoneSelect,
  NoneAppliesWarning,
  ZoneRewriteWarning,
  coverageUpdateError,
  UpdateErrorBanner,
} from './ZoneUpdateFields'

const M = 'lastmile_management.coverage_manager.update_modal'
const C = 'coberturas.createCoverages'

// Above this many hours, warn the operator to double-check the SLA value.
const SLA_WARN_HOURS = 72

const INPUT_CLS = 'block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'

const onlyDigits = s => s.replace(/[^0-9]/g, '')

const Helper = ({ children }) => <div className="mt-1 text-xs text-gray-400">{children}</div>

const HoursInput = ({ value, onChange, placeholder, suffix }) => (
  <div className="relative">
    <input
      type="text"
      inputMode="numeric"
      value={value}
      onChange={e => onChange(onlyDigits(e.target.value))}
      placeholder={placeholder}
      className={`${INPUT_CLS} pr-14 placeholder-gray-400`}
    />
    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 sm:text-sm">{suffix}</span>
  </div>
)

/**
 * Update flow for the "Un código postal" modal, with two tabs mirroring the
 * create form:
 *   "Un solo CP"  — search a CP, then edit its current config (pre-filled by id).
 *   "Rango de CP" — set SLA / offset / status / zone / extended zone and apply them to
 *                   a whole CP range via the update-by-range endpoint. No pre-fill
 *                   (bulk apply); status always applies, the rest only when filled.
 *
 * Zone and the extended-zone surcharge are two different columns, even though ops calls
 * both "extendida": `zone` is the tariff join key into shipping_costs.tarif_zone (and,
 * for Cubbo, the xdock routing key), while `extended_zone` is the ODA/OPA surcharge
 * added on top. Both default to "keep" so neither is rewritten by an SLA-only edit.
 */
export const UpdateSingleCoverageWizard = ({ shippingMethod, onCancel, onClose, onUpdated }) => {
  const { t } = useTranslation()

  const [mode, setMode] = useState('single') // "single" | "range"

  // --- single-CP flow (search → edit) ---
  const [step, setStep] = useState('search') // "search" | "edit"
  const [cp, setCp] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [coverage, setCoverage] = useState(null)
  const [sla, setSla] = useState('')
  const [slaOffset, setSlaOffset] = useState('')
  const [status, setStatus] = useState('active')
  // "" = keep. Not pre-filled from the coverage on purpose: re-sending the zone it is
  // already on would re-run ZoneUpdateValidator against it, so a misconfigured current
  // zone would start refusing unrelated SLA edits. The header shows both current values.
  const [zone, setZone] = useState('')
  const [extendedZone, setExtendedZone] = useState('') // "" | "true" | "false"

  // --- range flow (apply the same values to every CP in the range) ---
  const [rangeStart, setRangeStart] = useState('')
  const [rangeEnd, setRangeEnd] = useState('')
  const [rangeSla, setRangeSla] = useState('')
  const [rangeSlaOffset, setRangeSlaOffset] = useState('')
  const [rangeStatus, setRangeStatus] = useState('keep') // keep | active | paused
  const [rangeZone, setRangeZone] = useState('')
  const [rangeExtendedZone, setRangeExtendedZone] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [progressJobId, setProgressJobId] = useState(null)

  const range = useMemo(() => computeRange({ start: rangeStart, end: rangeEnd }), [rangeStart, rangeEnd])

  // The "all" tab delegates to UpdateCoverageForm, which offers no zone control (the
  // backend refuses a method-wide zone), so the list is not fetched for it.
  const { zones, isLoading: zonesLoading, error: zonesError } = useMethodZones(mode === 'all' ? null : shippingMethod?.id)

  // Single CP: the button is enabled only when the form differs from the
  // coverage's current values (no point submitting an unchanged update).
  const origSla = coverage?.delivery_sla_in_hours != null ? String(coverage.delivery_sla_in_hours) : ''
  const origOffset = coverage?.delivery_sla_offset != null ? String(coverage.delivery_sla_offset) : ''
  const origStatus = coverage?.enabled ? 'active' : 'paused'
  const singleHasChange = sla !== origSla || slaOffset !== origOffset || status !== origStatus || zone !== '' || extendedZone !== ''
  const singleSlaTooHigh = parseInt(sla, 10) > SLA_WARN_HOURS || parseInt(slaOffset, 10) > SLA_WARN_HOURS

  // Range: nothing changes while status is "keep" and SLA/offset are empty.
  const rangeHasChange = rangeStatus !== 'keep' || rangeSla.trim() !== '' || rangeSlaOffset.trim() !== '' || rangeZone !== '' || rangeExtendedZone !== ''
  const rangeSlaTooHigh = parseInt(rangeSla, 10) > SLA_WARN_HOURS || parseInt(rangeSlaOffset, 10) > SLA_WARN_HOURS

  const handleSearch = async () => {
    const value = cp.trim()
    if (!value) return
    setSearching(true)
    setSearchError(null)
    try {
      const res = await fetchShippingCoveragesList({
        shipping_method_id: shippingMethod.id,
        value_prefix: value,
        per_page: 100,
      })
      const match = (res?.coverages || []).find(c => String(c.value) === value)
      if (!match) {
        setSearchError(t(`${M}.single.not_found`))
        return
      }
      setCoverage(match)
      setSla(match.delivery_sla_in_hours != null ? String(match.delivery_sla_in_hours) : '')
      setSlaOffset(match.delivery_sla_offset != null ? String(match.delivery_sla_offset) : '')
      setStatus(match.enabled ? 'active' : 'paused')
      setStep('edit')
    } catch (e) {
      setSearchError(e?.response?.data?.error || e?.response?.data?.message || t(`${M}.error`))
    } finally {
      setSearching(false)
    }
  }

  const backToSearch = () => {
    setStep('search')
    setCoverage(null)
    setError(null)
    // Carrying the previous CP's zone choice into the next one would apply a
    // reclassification the operator picked for a different postal code.
    setZone('')
    setExtendedZone('')
  }

  // Single CP — update by id for precision.
  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const updateData = { enabled: status === 'active' }
      if (sla.trim() !== '') updateData.delivery_sla_in_hours = parseInt(sla, 10)
      if (slaOffset.trim() !== '') updateData.delivery_sla_offset = parseInt(slaOffset, 10)
      if (zone !== '') updateData.zone = zone
      if (extendedZone !== '') updateData.extended_zone = extendedZone === 'true'
      const res = await bulkUpdateShippingCoveragesByIds(shippingMethod.id, [coverage.id], updateData)
      if (res?.job_id) {
        setProgressJobId(res.job_id)
      } else {
        onUpdated?.()
        onClose?.()
      }
    } catch (e) {
      setError(coverageUpdateError(t, e))
    } finally {
      setSubmitting(false)
    }
  }

  // Range — apply the chosen values to every CP between start and end. Status
  // always applies; SLA/offset are sent only when filled (service drops empties).
  const handleSubmitRange = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const ranges = [
        {
          range_start: rangeStart,
          range_ends: rangeEnd,
          // "keep" → omit enabled so the status is left untouched.
          enabled: rangeStatus === 'keep' ? undefined : rangeStatus === 'active',
          delivery_sla: rangeSla,
          delivery_sla_offset: rangeSlaOffset,
          // "" => the service omits them, leaving each CP's zone and surcharge as is.
          zone: rangeZone,
          extended_zone: rangeExtendedZone,
        },
      ]
      const res = await updateShippingCoverageByRange(shippingMethod.id, ranges)
      if (res?.job_id) {
        setProgressJobId(res.job_id)
      } else {
        onUpdated?.()
        onClose?.()
      }
    } catch (e) {
      setError(coverageUpdateError(t, e))
    } finally {
      setSubmitting(false)
    }
  }

  // Job running / finished → show progress inline (replacing the wizard), so we
  // don't stack a second dialog on top of this modal.
  if (progressJobId) {
    return (
      <CoverageJobProgressPanel
        jobId={progressJobId}
        onClose={finalStatus => {
          setProgressJobId(null)
          if (finalStatus && finalStatus !== 'failed') {
            onUpdated?.()
            onClose?.()
          }
        }}
        onCompleted={() => onUpdated?.()}
      />
    )
  }

  const tabs = (
    <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
      {['single', 'range', 'all'].map(m => (
        <button
          key={m}
          type="button"
          onClick={() => {
            setMode(m)
            setError(null)
          }}
          className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            mode === m ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t(`${M}.tab_${m}`)}
        </button>
      ))}
    </div>
  )

  // ===== All-coverage tab (reuses the dedicated form; it manages its own
  // submit + progress, so it sits below the shared tab strip). =====
  if (mode === 'all') {
    return (
      <div className="space-y-5">
        {tabs}
        <UpdateCoverageForm shippingMethod={shippingMethod} onCancel={onCancel} onClose={onClose} onUpdated={onUpdated} />
      </div>
    )
  }

  // ===== Range tab =====
  if (mode === 'range') {
    const showRangeError = !range.valid && range.reason && rangeStart && rangeEnd
    return (
      <div className="space-y-5">
        {tabs}

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <FieldLabel required>{t(`${C}.fields.start`)}</FieldLabel>
            <input
              type="text"
              value={rangeStart}
              onChange={e => setRangeStart(e.target.value)}
              placeholder="01000"
              className={`${INPUT_CLS} placeholder-gray-400`}
            />
          </label>
          <label className="block">
            <FieldLabel required>{t(`${C}.fields.end`)}</FieldLabel>
            <input
              type="text"
              value={rangeEnd}
              onChange={e => setRangeEnd(e.target.value)}
              placeholder="01999"
              className={`${INPUT_CLS} placeholder-gray-400`}
            />
          </label>
        </div>

        <div className="min-h-5 text-sm">
          {range.valid && <span className="text-gray-600">{t(`${M}.range_preview`, { count: range.count })}</span>}
          {showRangeError && <span className="text-red-600">{t(`${C}.range.error.${range.reason}`, { max: MAX_RANGE, count: range.count })}</span>}
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">{t(`${M}.range_apply_title`)}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <FieldLabel>{t(`${M}.fields.sla`)}</FieldLabel>
              <HoursInput value={rangeSla} onChange={setRangeSla} placeholder={t(`${M}.fields.sla_placeholder`)} suffix={t(`${M}.fields.hours_suffix`)} />
              <Helper>{t(`${M}.fields.sla_help`)}</Helper>
            </div>

            <div>
              <FieldLabel>{t(`${M}.fields.sla_offset`)}</FieldLabel>
              <HoursInput
                value={rangeSlaOffset}
                onChange={setRangeSlaOffset}
                placeholder={t(`${M}.fields.sla_offset_placeholder`)}
                suffix={t(`${M}.fields.hours_suffix`)}
              />
              <Helper>{t(`${M}.fields.sla_offset_help`)}</Helper>
            </div>

            <div>
              <FieldLabel>{t(`${M}.fields.status`)}</FieldLabel>
              <select value={rangeStatus} onChange={e => setRangeStatus(e.target.value)} className={INPUT_CLS}>
                <option value="keep">{t(`${M}.fields.status_keep`)}</option>
                <option value="active">{t(`${M}.fields.status_active`)}</option>
                <option value="paused">{t(`${M}.fields.status_paused`)}</option>
              </select>
              <Helper>{t(`${M}.fields.status_help`)}</Helper>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ZoneSelect zones={zones} isLoading={zonesLoading} error={zonesError} value={rangeZone} onChange={setRangeZone} />
            <ExtendedZoneSelect zones={zones} value={rangeExtendedZone} onChange={setRangeExtendedZone} />
          </div>
        </div>

        {range.valid && <ZoneRewriteWarning zone={rangeZone} extendedZone={rangeExtendedZone} count={range.count} />}

        <NoneAppliesWarning zone={rangeZone} />

        {rangeSlaTooHigh && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{t(`${M}.sla_warning`)}</div>}

        <UpdateErrorBanner error={error} />

        <div className="flex gap-3 pt-1">
          <Button type="secondary" className="flex-1" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            type="primary"
            className="flex-1"
            disabled={!range.valid || !rangeHasChange || submitting}
            loading={submitting}
            loading_label={t(`${M}.submit`) + '…'}
            onClick={handleSubmitRange}
          >
            {t(`${M}.submit`)}
          </Button>
        </div>
      </div>
    )
  }

  // ===== Single tab · Step 1: search =====
  if (step === 'search') {
    return (
      <div className="space-y-5">
        {tabs}

        <label className="block">
          <FieldLabel required>{t(`${M}.single.search_label`)}</FieldLabel>
          <input
            type="text"
            value={cp}
            onChange={e => setCp(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && cp.trim() && !searching) handleSearch()
            }}
            placeholder={t(`${M}.single.search_placeholder`)}
            className={`${INPUT_CLS} placeholder-gray-400`}
            autoFocus
          />
          <Helper>{t(`${M}.single.search_help`)}</Helper>
        </label>

        {searchError && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{searchError}</div>}

        <div className="flex gap-3 pt-1">
          <Button type="secondary" className="flex-1" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            type="primary"
            className="flex-1"
            disabled={!cp.trim() || searching}
            loading={searching}
            loading_label={t(`${M}.single.searching`)}
            onClick={handleSearch}
          >
            {t(`${M}.single.search_button`)}
          </Button>
        </div>
      </div>
    )
  }

  // ===== Single tab · Step 2: edit =====
  return (
    <div className="space-y-5">
      {tabs}

      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
        <div className="text-sm">
          <span className="font-medium text-gray-900">CP {coverage.value}</span>
          {coverage.zone && <span className="text-gray-500"> · {coverage.zone}</span>}
          {/* The current surcharge is shown rather than pre-selected below: the selects
              stay on "keep" so an SLA-only edit never re-sends (and never re-validates)
              values the operator did not touch. */}
          <span className="text-gray-500">
            {' · '}
            {t(`${M}.fields.extended_zone`)}: {coverage.extended_zone ? t(`${M}.fields.extended_zone_yes`) : t(`${M}.fields.extended_zone_no`)}
          </span>
        </div>
        <button type="button" onClick={backToSearch} className="text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-700">
          {t(`${M}.single.change_cp`)}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <FieldLabel>{t(`${M}.fields.sla`)}</FieldLabel>
          <HoursInput value={sla} onChange={setSla} placeholder={t(`${M}.fields.sla_placeholder`)} suffix={t(`${M}.fields.hours_suffix`)} />
          <Helper>{t(`${M}.fields.sla_help`)}</Helper>
        </div>

        <div>
          <FieldLabel>{t(`${M}.fields.sla_offset`)}</FieldLabel>
          <HoursInput value={slaOffset} onChange={setSlaOffset} placeholder={t(`${M}.fields.sla_offset_placeholder`)} suffix={t(`${M}.fields.hours_suffix`)} />
          <Helper>{t(`${M}.fields.sla_offset_help`)}</Helper>
        </div>

        <div>
          <FieldLabel required>{t(`${M}.fields.status`)}</FieldLabel>
          <select value={status} onChange={e => setStatus(e.target.value)} className={INPUT_CLS}>
            <option value="active">{t(`${M}.fields.status_active`)}</option>
            <option value="paused">{t(`${M}.fields.status_paused`)}</option>
          </select>
          <Helper>{t(`${M}.fields.status_help`)}</Helper>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ZoneSelect zones={zones} isLoading={zonesLoading} error={zonesError} value={zone} onChange={setZone} />
        <ExtendedZoneSelect zones={zones} value={extendedZone} onChange={setExtendedZone} />
      </div>

      <NoneAppliesWarning zone={zone} />

      {singleSlaTooHigh && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{t(`${M}.sla_warning`)}</div>}

      <UpdateErrorBanner error={error} />

      <div className="flex gap-3 pt-1">
        <Button type="secondary" className="flex-1" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          type="primary"
          className="flex-1"
          disabled={!singleHasChange || submitting}
          loading={submitting}
          loading_label={t(`${M}.submit`) + '…'}
          onClick={handleSubmit}
        >
          {t(`${M}.submit`)}
        </Button>
      </div>
    </div>
  )
}
